/**
 * Marketing i18n fragment — intgTopdesk (integrations/topdesk).
 *
 * Source of truth for every claim on this page: content/docs/{en,nl}/admin/
 * issue-trackers.md § "TOPdesk" and the shipped code under
 * src/server/issue-trackers/topdesk* + src/server/integrations/topdesk/. Only
 * shipped behaviour is described: incident create/link as a first-line
 * incident, processing-status read-back, the three opt-in outbound toggles
 * (progress notes, status push on resolve, customer reply on resolve — all off
 * by default), the approval-gated caller-visible reply, the caller as the
 * customer link, the self-configured Events & Actions callback that is a
 * trigger rather than a source of truth, and settled incidents as retrieval
 * evidence. Deliberately NOT claimed: priority/category/label mirroring,
 * attachment sync and scheduled polling (none of these exist).
 */

export const intgTopdeskEn = {
  meta: {
    title: "TOPdesk integration — incident sync, approved caller replies and support evidence",
    description:
      "Create and link TOPdesk incidents, read the processing status back instead of guessing it, post operator-only progress notes, and send a caller-visible reply only after a person approved it. Every outbound path is off by default, health is tracked per capability, and settled incidents become evidence for the next answer.",
  },
  hero: {
    eyebrow: "TOPdesk integration",
    title: "Incidents linked to TOPdesk,",
    highlight: "caller replies held for approval",
    subtitle:
      "FixControl creates and links incidents in TOPdesk and reads their processing status back, so the service desk stays the system of record for the incident and the conversation with the caller. A reply to a customer of your organization is drafted by AI and held until someone on your team approves it — only then is it posted as an incident action the caller can see.",
    primaryCta: "Book a demo",
    secondaryCta: "See approved replies",
    bullet1: "Caller replies held for approval",
    bullet2: "Outbound paths off by default",
    bullet3: "Per-capability health",
  },
  what: {
    eyebrow: "What it does",
    title: "What the TOPdesk connection covers",
  },
  features: {
    createLink: {
      title: "Incident create and link",
      body: "An approved FixControl issue becomes a first-line TOPdesk incident: title as the brief description, body as the request, and the operator group from the project binding when one is configured. The FixControl issue key travels in the incident’s external number; the issue keeps the incident number and a deep link.",
    },
    statusReadBack: {
      title: "Processing status read back, never guessed",
      body: "Processing statuses are configured per TOPdesk instance, so FixControl reads the incident’s status back over the API instead of assuming a name. Resolving an issue marks the incident completed and, where you named one, sets the processing status you chose — an incident the service desk already settled is left alone.",
    },
    approvedReplies: {
      title: "Approved caller-visible replies",
      body: "Resolving an issue can queue a reply draft in the review queue. A person approves it, and only then is it posted as an incident action visible to the caller. Reject or escalate posts nothing.",
    },
    internalNotes: {
      title: "Operator-only progress notes",
      body: "Engineering progress — a patch ready, a pull request opened, with its link — can be posted as incident actions marked invisible to the caller. Operators only, and one-way.",
    },
    callerLink: {
      title: "The caller identifies the customer",
      body: "On a TOPdesk incident the caller plays the role a requester plays on a helpdesk ticket. Linking a customer to a TOPdesk-linked issue sets the caller on the incident, so the customer record and the incident agree on who is asking.",
    },
    supportContext: {
      title: "Settled incidents as evidence",
      body: "Triage pulls a scoped set of related settled incidents, each with why it matched and a trust score, to weigh rather than to trust blindly. Only actions visible to the caller can count as a resolution; actions marked invisible to the caller are excluded, fail-closed.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Which TOPdesk paths are working, shown per path",
    description:
      "Creating an incident, posting a caller-visible reply, writing an operator-only note, pushing a status and setting the caller are separate write paths with separate requirements, and each records its own health. A working incident create can never disguise a broken reply path behind one connected badge.",
    readIncidents: {
      name: "Read incidents, status & actions",
      body: "Reads incidents, their processing status and their actions within the linked project, to build support context and answer questions.",
    },
    createLink: {
      name: "Incident create & link",
      body: "Creates and links first-line incidents under the application password of the operator you nominate. An operator group bound to a project routes incoming incidents deterministically — FixControl never guesses a project.",
    },
    callerReply: {
      name: "Caller-visible reply",
      body: "Off by default and switched on per organization; even then the draft is held until a person approves it. Caller-visible actions and internal ones share one action stream and differ only in a visibility flag, which is exactly why the visible one sits behind the approval flow.",
    },
    callerLink: {
      name: "Set the caller on an incident",
      body: "Linking a customer looks the person up on an exact e-mail match and only proceeds on a single hit. No match, or more than one, is a visible failure — FixControl never creates a person record in your instance to make a link succeed.",
    },
    inboundWebhook: {
      name: "Inbound status updates",
      body: "Needs a configured shared secret and an Events &amp; Actions HTTP action you author yourself; without the secret the endpoint is disabled. The incident is then always read back over the API.",
    },
    internalNote: {
      name: "Operator-only action",
      body: "Off by default. Where enabled, progress is posted as an action marked invisible to the caller — for operators and engineers, never in the caller’s view.",
    },
    statusPush: {
      name: "Status push on resolve",
      body: "Off by default. Resolving marks the incident completed, and sets a processing status only when you name one from your own instance’s list — FixControl never invents a status name.",
    },
    notMirrored: {
      name: "Priority, categories, labels & attachments",
      body: "Not mirrored. Priority, urgency, impact, category and subcategory are configuration records in your own instance, a TOPdesk incident has no free-form label field, and attachments are not synced. The FixControl priority stays readable in the incident request text.",
    },
  },
  governed: {
    eyebrow: "When something changes in TOPdesk",
    title: "Your callback is a trigger, not the truth",
    noteTitle: "Read back first, act second",
    noteBody:
      "TOPdesk has no webhooks an application can register for you, so you author the callback yourself in Events &amp; Actions — which is precisely why FixControl always reads the incident back over the API before acting on it. Only the issue’s cached tracker status is synced — never the FixControl workflow status. A settled incident returning to an open status notifies your managers and flags the issue as reopened; it does not re-run the work. The endpoint stays disabled until you configure a shared secret, there is no scheduled polling, and every outbound TOPdesk write goes through the integration outbox, retrying transient failures and dead-lettering persistent ones onto the operational timeline.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the TOPdesk integration",
    q1: {
      q: "Does FixControl reply to callers automatically?",
      a: "No. The reply path is off by default and switched on per organization, and even then a reply starts as an AI draft that is held until a person approves it. The caller-visible action is posted after the approval is recorded — not before.",
    },
    q2: {
      q: "What keeps an internal note from reaching the caller?",
      a: "Internal notes and caller-visible replies travel through the same TOPdesk action stream and differ only in a visibility flag. FixControl treats them as two separate write paths: the operator-only one is a plain opt-in toggle, the caller-visible one exists only behind the approval flow. When reading, the same flag decides what may count as a resolution.",
    },
    q3: {
      q: "Which permissions does the TOPdesk operator need?",
      a: "An application password for the operator FixControl acts as, with API access, read, create and edit on first- and second-line incidents, and read on Persons to resolve the caller. An expired or revoked application password is reported as an authentication failure that needs a reconnect, not as a temporary outage.",
    },
    q4: {
      q: "How do updates from TOPdesk reach FixControl?",
      a: "Through an Events &amp; Actions HTTP action you configure on incident changes, posting to a FixControl endpoint with a shared secret; without a configured secret that endpoint is disabled. Because you author that payload, it is treated as a trigger and the incident is read back over the API — a hand-authored notification cannot move a status on its own. There is no scheduled polling.",
    },
    q5: {
      q: "Are priority, categories and attachments mirrored?",
      a: "No. Priority, urgency, impact, category and subcategory are configuration records in your own instance, and FixControl neither creates nor guesses them — the FixControl priority stays readable in the incident request text instead. Incidents carry no free-form label field, and attachments are not synced.",
    },
  },
  related: {
    freshdesk: {
      label: "Freshdesk",
      description: "The same governed reply flow on a Freshdesk helpdesk.",
    },
    odoo: {
      label: "Odoo Helpdesk",
      description: "Tickets, chatter replies after approval and stage read-back.",
    },
    approvedReplies: {
      label: "Approved replies",
      description: "The approval flow behind every customer-facing reply.",
    },
  },
  cta: {
    title: "Watch a TOPdesk reply wait for approval",
    description:
      "Book a demo: an issue resolves, AI drafts the reply on the linked incident, and the caller-visible action waits until someone approves it.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgTopdeskNl = {
  meta: {
    title: "TOPdesk-integratie — incidentkoppeling, goedgekeurde antwoorden aan de caller en supportbewijs",
    description:
      "Maak en koppel TOPdesk-incidenten, lees de behandelstatus terug in plaats van hem te gokken, plaats acties die alleen operators zien, en stuur een antwoord aan de caller pas nadat iemand het heeft goedgekeurd. Elk uitgaand pad staat standaard uit, health wordt per capability bijgehouden, en afgeronde incidenten worden bewijs voor het volgende antwoord.",
  },
  hero: {
    eyebrow: "TOPdesk-integratie",
    title: "Incidenten gekoppeld aan TOPdesk,",
    highlight: "antwoorden wachten op goedkeuring",
    subtitle:
      "FixControl maakt en koppelt incidenten in TOPdesk en leest hun behandelstatus terug, zodat de servicedesk het systeem van vastlegging blijft voor het incident en het gesprek met de caller. Een antwoord aan een klant van je organisatie wordt door AI opgesteld en vastgehouden tot iemand van je team het goedkeurt — pas dan verschijnt het als incidentactie die de caller kan zien.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk goedgekeurde antwoorden",
    bullet1: "Antwoorden aan de caller wachten op goedkeuring",
    bullet2: "Uitgaande paden standaard uit",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de TOPdesk-koppeling dekt",
  },
  features: {
    createLink: {
      title: "Incident aanmaken en koppelen",
      body: "Een goedgekeurd FixControl-issue wordt een eerstelijns TOPdesk-incident: titel als korte omschrijving, tekst als verzoek, en de behandelaarsgroep uit de projectkoppeling als je die instelt. De FixControl-issuesleutel reist mee in het externe nummer van het incident; het issue bewaart het incidentnummer en een directe link.",
    },
    statusReadBack: {
      title: "Behandelstatus teruggelezen, nooit gegokt",
      body: "Behandelstatussen richt je per TOPdesk-omgeving zelf in, dus FixControl leest de status van het incident terug via de API in plaats van een naam aan te nemen. Het oplossen van een issue meldt het incident gereed en zet daarnaast de behandelstatus die je zelf benoemt — een incident dat de servicedesk al had afgerond blijft ongemoeid.",
    },
    approvedReplies: {
      title: "Goedgekeurde antwoorden aan de caller",
      body: "Het oplossen van een issue kan een conceptantwoord in de reviewwachtrij zetten. Iemand keurt het goed, en pas dan verschijnt het als incidentactie die zichtbaar is voor de caller. Afwijzen of escaleren plaatst niets.",
    },
    internalNotes: {
      title: "Voortgangsnotities alleen voor operators",
      body: "Voortgang uit engineering — een patch klaar, een pull request geopend, met de link erbij — kan als incidentactie komen die als onzichtbaar voor de caller is gemarkeerd. Alleen voor operators, en in één richting.",
    },
    callerLink: {
      title: "De caller koppelt de klant",
      body: "Op een TOPdesk-incident speelt de caller de rol die een verzoeker op een helpdeskticket speelt. Een klant koppelen aan een issue met TOPdesk-incident zet die caller op het incident, zodat het klantgegeven en het incident het eens zijn over wie er vraagt.",
    },
    supportContext: {
      title: "Afgeronde incidenten als bewijs",
      body: "Bij triage haalt FixControl een afgebakende set verwante afgeronde incidenten op, elk met de reden van de match en een betrouwbaarheidsscore — om te wegen, niet om blind te volgen. Alleen acties die zichtbaar zijn voor de caller kunnen als oplossing tellen; acties die als onzichtbaar zijn gemarkeerd vallen af, fail-closed.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Welke TOPdesk-paden werken, per pad getoond",
    description:
      "Een incident aanmaken, een antwoord aan de caller plaatsen, een operatoractie schrijven, een status zetten en de caller invullen zijn aparte schrijfpaden met eigen vereisten, en elk pad legt zijn eigen health vast. Een werkend aanmaakpad kan zo nooit een kapot antwoordpad verbergen achter één ‘connected’-badge.",
    readIncidents: {
      name: "Incidenten, status & acties lezen",
      body: "FixControl leest incidenten, hun behandelstatus en hun acties binnen het gekoppelde project, om support-context samen te stellen en vragen te beantwoorden.",
    },
    createLink: {
      name: "Incident aanmaken & koppelen",
      body: "Aanmaken en koppelen van eerstelijns incidenten gebeurt onder het applicatiewachtwoord van de operator die je aanwijst. Een behandelaarsgroep die aan een project is gekoppeld, routeert binnenkomende incidenten voorspelbaar — FixControl gokt nooit een project.",
    },
    callerReply: {
      name: "Antwoord zichtbaar voor de caller",
      body: "Standaard uit en per organisatie aan te zetten; ook dan blijft het concept vastgehouden tot iemand het goedkeurt. Zichtbare en interne acties delen één actiestroom en verschillen alleen in een zichtbaarheidsvlag — precies waarom de zichtbare achter de goedkeuringsroute staat.",
    },
    callerLink: {
      name: "De caller op een incident zetten",
      body: "Een klant koppelen zoekt de persoon op een exacte e-mailmatch en gaat alleen door bij precies één treffer. Geen match, of meer dan één, is een zichtbare fout — FixControl maakt nooit een persoonsrecord aan in je omgeving om een koppeling te laten slagen.",
    },
    inboundWebhook: {
      name: "Inkomende statusupdates",
      body: "Vereist een ingesteld gedeeld geheim en een HTTP-action onder Events &amp; Actions die je zelf opstelt; zonder dat geheim staat het endpoint uit. Het incident wordt daarna altijd via de API teruggelezen.",
    },
    internalNote: {
      name: "Actie alleen voor operators",
      body: "Standaard uit. Waar aangezet komt voortgang binnen als actie die is gemarkeerd als onzichtbaar voor de caller — voor operators en engineers, nooit in het beeld van de caller.",
    },
    statusPush: {
      name: "Status zetten bij oplossen",
      body: "Standaard uit. Oplossen meldt het incident gereed, en zet alleen een behandelstatus als je er zelf een benoemt uit de lijst van je eigen omgeving — FixControl verzint nooit een statusnaam.",
    },
    notMirrored: {
      name: "Prioriteit, categorieën, labels & bijlagen",
      body: "Worden niet gespiegeld. Prioriteit, urgentie, impact, categorie en subcategorie zijn configuratierecords in je eigen omgeving, een TOPdesk-incident heeft geen vrij labelveld, en bijlagen worden niet gesynchroniseerd. De FixControl-prioriteit blijft leesbaar in de verzoektekst van het incident.",
    },
  },
  governed: {
    eyebrow: "Als er iets verandert in TOPdesk",
    title: "Jouw callback is een trigger, niet de waarheid",
    noteTitle: "Eerst teruglezen, dan handelen",
    noteBody:
      "TOPdesk kent geen webhooks die een applicatie voor je kan registreren, dus je richt de callback zelf in via Events &amp; Actions — juist daarom leest FixControl het incident altijd terug via de API voordat het handelt. Alleen de gecachete trackerstatus van het issue wordt gesynchroniseerd — nooit de FixControl-workflowstatus. Gaat een afgerond incident terug naar een open status, dan worden je managers gewaarschuwd en krijgt het issue een heropend-markering; het werk wordt niet opnieuw uitgevoerd. Het endpoint blijft uit tot je een gedeeld geheim instelt, er wordt niet periodiek gepold, en elke uitgaande TOPdesk-schrijfactie loopt via de integratie-outbox, die tijdelijke fouten opnieuw probeert en hardnekkige naar de dead-letter op de operationele tijdlijn zet.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de TOPdesk-integratie",
    q1: {
      q: "Antwoordt FixControl automatisch aan callers?",
      a: "Nee. Het antwoordpad staat standaard uit en zet je per organisatie aan, en ook dan begint een antwoord als AI-concept dat wordt vastgehouden tot iemand het goedkeurt. De voor de caller zichtbare actie verschijnt nadat de goedkeuring is vastgelegd — niet ervoor.",
    },
    q2: {
      q: "Wat houdt een interne notitie bij de caller vandaan?",
      a: "Interne notities en antwoorden aan de caller lopen door dezelfde TOPdesk-actiestroom en verschillen alleen in een zichtbaarheidsvlag. FixControl behandelt ze als twee aparte schrijfpaden: die voor operators is een gewone opt-in-schakelaar, die voor de caller bestaat alleen achter de goedkeuringsroute. Bij lezen bepaalt dezelfde vlag wat als oplossing mag tellen.",
    },
    q3: {
      q: "Welke rechten heeft de TOPdesk-operator nodig?",
      a: "Een applicatiewachtwoord voor de operator waaronder FixControl handelt, met API-toegang, lezen, aanmaken en bewerken op eerste- en tweedelijns incidenten, en leesrecht op Persoonsgegevens om de caller op te zoeken. Een verlopen of ingetrokken applicatiewachtwoord wordt gemeld als een authenticatiefout die om opnieuw verbinden vraagt, niet als een tijdelijke storing.",
    },
    q4: {
      q: "Hoe bereiken updates uit TOPdesk FixControl?",
      a: "Via een HTTP-action onder Events &amp; Actions die je zelf op incidentwijzigingen instelt en die naar een FixControl-endpoint post met een gedeeld geheim; zonder ingesteld geheim staat dat endpoint uit. Omdat je dat bericht zelf opstelt, geldt het als trigger en wordt het incident via de API teruggelezen — een zelfgeschreven melding kan op zichzelf geen status verzetten. Er wordt niet periodiek gepold.",
    },
    q5: {
      q: "Worden prioriteit, categorieën en bijlagen gespiegeld?",
      a: "Nee. Prioriteit, urgentie, impact, categorie en subcategorie zijn configuratierecords in je eigen omgeving, en FixControl maakt of gokt ze niet — de FixControl-prioriteit blijft in plaats daarvan leesbaar in de verzoektekst van het incident. Incidenten hebben geen vrij labelveld, en bijlagen worden niet gesynchroniseerd.",
    },
  },
  related: {
    freshdesk: {
      label: "Freshdesk",
      description: "Dezelfde gegoverneerde antwoordroute op een Freshdesk-helpdesk.",
    },
    odoo: {
      label: "Odoo Helpdesk",
      description: "Tickets, chatterantwoorden na goedkeuring en stage teruglezen.",
    },
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "De goedkeuringsroute achter elk antwoord aan een klant.",
    },
  },
  cta: {
    title: "Zie een TOPdesk-antwoord wachten op goedkeuring",
    description:
      "Boek een demo: een issue wordt opgelost, AI stelt het antwoord op het gekoppelde incident op, en de voor de caller zichtbare actie wacht tot iemand het goedkeurt.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
